import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Paper, Stack, Button} from 'views/components';
import {Translate, MarkdownViewer} from 'views/module';
import {
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarTodayIcon,
  CreditCardOutlined as CreditCardOutlinedIcon,
} from 'views/icons';
import {SecureImageDisplay} from 'views/module/secureStorage/DisplayComponents';

import moment from 'moment-timezone';

import EventTimelineTile from './EventTimelineTile';
import UserTile from './UserTile';
import backend from 'utils/backend';

import ReimbursementTileSkeleton from '../skeleton_loaders/ReimbursementTileSkeleton';

const EventDetailTile = (props) => {
  const {setModel, event, statusProgressWrap: progressWrap} = props;
  const [expenses, setExpenses] = React.useState({});
  const [expensesLoading, setExpensesLoading] = React.useState(true);

  React.useEffect(() => {
    setExpensesLoading(true);
    progressWrap(async () => {
      const reimbursement = await backend.expenseReimbursement.getExpenseReimbursement(event.eventRn);
      setExpenses(reimbursement);
      setExpensesLoading(false);
    }, {showProgress: false});
  }, [event.eventRn]);

  const clientData = event.clientData.clientData.clientDetail;
  const caregiverData = event.caregiverData.caregiverDetail;

  const userTimezone = moment.tz.guess();
  const startDateTime = moment.tz(event.eventData.startTime, event.eventData.timezone).tz(userTimezone);
  const endDateTime = moment.tz(event.eventData.endTime, event.eventData.timezone).tz(userTimezone);
  const formattedDate = startDateTime.format('dddd, MMMM D, YYYY');
  const startTime = startDateTime.format('h:mm A');
  const endTime = endDateTime.format('h:mm A');
  const durationHours = parseFloat((endDateTime.diff(startDateTime, 'hours', true)).toFixed(1));
  const timezoneAbbr = startDateTime.format('z');

  return (
    <Paper
      elevation={8}
      onClick={(e) => e.stopPropagation()}
      sx={{
        boxSizing: 'border-box',
        borderRadius: 3,
        p: {xs: 0.5, lg: 3},
        transition: 'left 0.3s ease-in-out',
        animation: 'slideIn 0.3s ease-out',
        height: {
          xs: 'auto',
          sm: 'auto',
          md: '80vh',
          lg: '80vh',
        },
        overflowY: {
          xs: 'visible',
          sm: 'visible',
          md: 'auto',
          lg: 'auto',
        },
        overflowX: 'hidden',
        maxWidth: {xs: '100vw', md: '1200px', lg: '1200px'},
        width: {xs: '100%', md: '100%', lg: '100%'},
        alignSelf: 'center',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
        sx={{
          minHeight: {xs: 64, sm: 64, md: 64, lg: 'auto'},
          borderBottom: {xs: '1px solid', sm: '1px solid', md: '1px solid', lg: 'none'},
          borderColor: {xs: 'grey.200', sm: 'grey.200', md: 'grey.200', lg: 'transparent'},
          pb: {xs: 1.5, sm: 1.5, md: 1.5, lg: 0},
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: {xs: 700, sm: 700, md: 700, lg: 600},
            textAlign: {xs: 'center', sm: 'center', md: 'center', lg: 'left'},
            letterSpacing: 0.5,
            color: 'text.primary',
            width: '100%',
            lineHeight: 1.2,
            wordBreak: 'break-word',
            pr: 2,
          }}
        >
          {event.info.title}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} sx={{ml: 2}}>
          <Button
            variant="contained"
            type="button"
            onClick={() => setModel('RESCHEDULE')}
          >
            <Translate expression="eventList.eventDetail.reschedule" defaultText="Reschedule" />
          </Button>
          <Button
            variant="contained"
            color="error"
            type="button"
            onClick={() => setModel('CANCEL')}
          >
            <Translate expression="eventList.eventDetail.cancel" defaultText="Cancel" />
          </Button>
        </Box>
      </Box>

      {/* Responsive Stack for UserTiles */}
      <Stack
        direction={{xs: 'column', sm: 'column', md: 'column', lg: 'row'}}
        spacing={2}
        mb={3}
      >
        {/* Client Information */}
        <UserTile
          title="eventList.eventDetail.clientInformation"
          userData={clientData}
          nameField="clientName"
        />

        {/* Caregiver Information */}
        <UserTile
          title="eventList.eventDetail.caregiverInformation"
          userData={caregiverData}
          nameField="caregiverName"
        />
      </Stack>

      <Paper sx={{p: 2, mb: 2, borderRadius: 4}}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 2}}>
          <AccessTimeIcon sx={{width: 24, height: 24, color: '#b0b0b0'}}/>
          <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center'}}>
            <Translate expression='eventList.eventDetail.TimeSection.Title' />
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarTodayIcon fontSize="small" />
          <Typography>
            {formattedDate}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} mt={1}>
          <AccessTimeIcon fontSize="small" />
          <Typography>
            {startTime} - {endTime} {timezoneAbbr}
          </Typography>
          <Typography color="text.secondary" ml={2}>
            {durationHours} <Translate expression={durationHours === 1 ?
                'eventList.eventDetail.TimeSection.hour' : 'eventList.eventDetail.TimeSection.hours'} />
          </Typography>
        </Stack>
      </Paper>
      <Paper sx={{border: '1px solid', borderColor: 'grey.200', borderRadius: 2, p: 2, mb: 3}}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="subtitle1" sx={{fontWeight: 600, display: 'flex', alignItems: 'center'}}>
            <DescriptionIcon sx={{mr: 1, color: 'grey.500'}} />
            <Translate expression='eventList.eventDetail.notes' />
          </Typography>
        </Box>
        <Box
          sx={{
            background: '#fafafa',
            borderRadius: 2,
            p: 2,
          }}
        >
          {
            event.info.description.blob &&
                <MarkdownViewer
                  value={event.info.description.blob}
                  truncate={false}
                  style={{fontStyle: 'italic', color: 'text.secondary', background: 'transparent', boxShadow: 'none', padding: 0, margin: 0}}
                />
          }
          {
            !event.info.description.blob &&
              <Typography variant="body2" sx={{fontStyle: 'italic', color: 'text.secondary'}}>
                <Translate expression="eventList.eventDetail.noNotes" />
              </Typography>
          }
        </Box>
      </Paper>
      {
        (expensesLoading || expenses.expenseReimbursement) &&
          <Paper sx={{border: '1px solid', borderColor: 'grey.200', borderRadius: 2, p: 2, mb: 3}}>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="subtitle1" sx={{fontWeight: 600, display: 'flex', alignItems: 'center'}}>
                <CreditCardOutlinedIcon sx={{mr: 1, color: 'grey.500'}} />
                <Translate expression='eventList.ReimbursementSection.Title' />
              </Typography>
            </Box>
            {
              expensesLoading && <ReimbursementTileSkeleton />
            }
            {
              !expensesLoading &&
              expenses.expenseReimbursement.expenses.map((exp, idx) => (
                <Box
                  key={idx}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header: title and amount */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 3,
                      pt: 2,
                      pb: 1,
                    }}
                  >
                    <Typography variant="h6" sx={{fontWeight: 600}}>
                      {exp.title}
                    </Typography>
                    <Typography variant="h6" sx={{fontWeight: 700}}>
                      <Translate expression='eventList.ReimbursementSection.Amount' substitute={{expenses: exp.price}}/>
                    </Typography>
                  </Box>

                  {/* Images row */}
                  <Box
                    sx={{
                      px: 3,
                      pb: 2,
                    }}
                  >
                    <SecureImageDisplay
                      images={exp.pictures.map((src) => ({src}))}
                      sx={{marginTop: 1}}
                    />
                  </Box>
                </Box>
              ))
            }
          </Paper>
      }

      <EventTimelineTile
        event={event}
        setModel={setModel}
      />
    </Paper>
  );
};

EventDetailTile.propTypes = {
  setModel: PropTypes.func.isRequired,
  event: PropTypes.object,
  statusProgressWrap: PropTypes.func.isRequired,
};

export default EventDetailTile;
